import { State, Action, StateContext, Selector } from "@ngxs/store";
import { Status, Sample, SampleService, EvaluationService, translateCommonErrorStatus, Evaluation, OrganizationProtocolPreference, FilterBuilder, ProtocolFilter, CustomProtocolService, ScheduledAction, ScheduledActionService, StorageRegime } from '@core/data';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable, Inject } from "@angular/core";
import { Library, Protocol, SampleType } from "@library";
import { OrganizationProtocolPreferencesService } from "@core/data/api/organization-protocol-preferences.service";
import { Snackbar } from "@core/material";
import { LIBRARY } from '@app/evaluation/library';
import { scheduledActionsFromStorageRegime } from '../sample-form/storage-regime-schedule';


export interface EvaluationScheduleFormStateModel {
    orgKey: string;
    status: Status;
    sampleKey: string;
    bulkKeys: string[];
    cropId: string,
    cropIds: string[],
    sampleType: SampleType,
    sample: Sample;
    result: Evaluation[];
    protocolOptions: Protocol[];
    preferences: OrganizationProtocolPreference[];
}

const DEFAULTS: EvaluationScheduleFormStateModel = {
    orgKey: null,
    status: Status.UNINITIALIZED,
    sampleKey: null,
    bulkKeys: [],
    cropId: null,
    cropIds: [],
    sampleType: null,
    sample: null,
    result: null,
    protocolOptions: [],
    preferences: [],
};


export class InitEvaluationScheduleForm {
    static readonly type = '[EvaluationScheduleForm] Init';
    constructor(public sampleKey: string, public orgKey: string){}
}

export class InitBulkScheduleForm {
    static readonly type = '[EvaluationScheduleForm] Init Bulk';
    constructor(public sampleKeys: string[], public orgKey: string, public cropId: string, public sampleType: SampleType, public cropIds: string[] = []){}
}

export class SubmitEvaluationScheduleForm {
    static readonly type = '[EvaluationScheduleForm] Submit';
    constructor(
        public evals: Partial<Evaluation>[],
        // Custom-Regime-only — a Legacy Library schedule never populates an entry here.
        public regimesBySampleKey: Record<string, StorageRegime> = {},
        // Whatever the form's datepicker control emits (moment/Date/string) — passed straight into moment() by scheduledActionsFromStorageRegime().
        public startDate: any = null,
    ){}
}

export class LoadOrganisationPreference {
    static readonly type = '[EvaluationScheduleForm] Load Preference';
    constructor(public orgKey: string) {}
}

@State<EvaluationScheduleFormStateModel>({
    name: 'evaluation_schedule_form',
    defaults: DEFAULTS,
})
@Injectable()
export class EvaluationScheduleFormState {


    @Selector()
    static sample(state: EvaluationScheduleFormStateModel){
        return state.sample;
    }

    constructor(
        private _snackbar: Snackbar,
        private _sampleService: SampleService,
        private _evalService: EvaluationService,
        private _scheduledActionService: ScheduledActionService,
        private _protocol_preferencesService: OrganizationProtocolPreferencesService,
        private _customProtocolService: CustomProtocolService,
        @Inject(LIBRARY) private _library: Library,
    ){}

    @Action(InitEvaluationScheduleForm, {cancelUncompleted: true})
    initEvaluationScheduleForm(ctx: StateContext<EvaluationScheduleFormStateModel>, action: InitEvaluationScheduleForm){

        ctx.patchState({
            ...DEFAULTS,
            status: Status.LOADING,
            sampleKey: action.sampleKey
        });

        return this._sampleService.get(action.sampleKey, {related: ['scionCultivar']})
                .pipe(tap(
                    sample => {

                        ctx.patchState({
                            sample,
                            orgKey: action.orgKey,
                            sampleType: sample.type,
                            cropId: sample.scionCultivar.cropId,
                        });

                        ctx.dispatch(new LoadOrganisationPreference(action.orgKey));

                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        })
                    }
                ));

    }

    @Action(InitBulkScheduleForm)
    initBulkScheduleForm(ctx: StateContext<EvaluationScheduleFormStateModel>, action: InitBulkScheduleForm) {
        ctx.patchState({
            ...DEFAULTS,
            status: Status.LOADING,
            bulkKeys: action.sampleKeys,
            cropId: action.cropId,
            cropIds: action.cropIds,
            orgKey: action.orgKey,
            sampleType: action.sampleType,
        });

        return ctx.dispatch(new LoadOrganisationPreference(action.orgKey));
    }


    @Action(SubmitEvaluationScheduleForm)
    submitEvaluationScheduleForm(ctx: StateContext<EvaluationScheduleFormStateModel>, action: SubmitEvaluationScheduleForm) {

        ctx.patchState({
            status: Status.LOADING,
        });

        return this._evalService.createMany({evals: action.evals})
            .pipe(
                switchMap(createdEvaluations => {
                    const scheduledActions = this.scheduledActionsForSubmission(action, createdEvaluations);

                    if (!scheduledActions.length) return of(createdEvaluations);

                    // A Scheduled Action failure must not hide that the Evaluations themselves
                    // were created — the submission still completes with its real (Evaluation) result.
                    return this._scheduledActionService.createMany({ actions: scheduledActions }).pipe(
                        map(() => createdEvaluations),
                        catchError(() => {
                            this._snackbar.error('Evaluations were scheduled, but their Scheduled Actions could not be fully saved. Check them before adding any manually.');
                            return of(createdEvaluations);
                        }),
                    );
                }),
                tap(
                    result => {

                        ctx.patchState({
                            status: Status.COMPLETE,
                            result
                        });

                    },
                    error => {
                        ctx.patchState({
                            status: translateCommonErrorStatus(error)
                        });
                    }
                ));

    }

    // Groups the (now submission-order-preserving) created-Evaluations response by sampleKey,
    // resolves each Custom-Regime sample's own stageKey->evalKey map from its slice, then
    // generates that sample's Scheduled/Sample-level Actions (two-pass resolution,
    // stages resolved before actions) — a Legacy-schedule sample is skipped entirely, it has no entry in
    // regimesBySampleKey.
    private scheduledActionsForSubmission(action: SubmitEvaluationScheduleForm, createdEvaluations: Evaluation[]): Partial<ScheduledAction>[] {
        const evaluationsBySample = new Map<string, Evaluation[]>();

        (createdEvaluations || []).forEach(evalu => {
            const forSample = evaluationsBySample.get(evalu.sampleKey) || [];
            forSample.push(evalu);
            evaluationsBySample.set(evalu.sampleKey, forSample);
        });

        return Object.keys(action.regimesBySampleKey || {}).reduce((all, sampleKey) => {
            // Guards against a stale regimesBySampleKey entry not actually part of this submission.
            if (!evaluationsBySample.has(sampleKey)) return all;

            const regime = action.regimesBySampleKey[sampleKey];
            const stages = regime.config?.evaluations || [];
            const sampleEvaluations = evaluationsBySample.get(sampleKey) || [];

            const evaluationsByStageKey: Record<string, string> = {};
            sampleEvaluations.forEach((evalu, index) => {
                const stage = stages[index] as { stageKey?: string };
                if (stage?.stageKey && evalu?.key) evaluationsByStageKey[stage.stageKey] = evalu.key;
            });

            return all.concat(scheduledActionsFromStorageRegime(sampleKey, regime, evaluationsByStageKey, action.startDate));
        }, [] as Partial<ScheduledAction>[]);
    }

    @Action(LoadOrganisationPreference)
    loadOrganisationPreference(ctx: StateContext<EvaluationScheduleFormStateModel>, action: LoadOrganisationPreference) {
        const state = ctx.getState();

        ctx.patchState({
            status: Status.LOADING,
        })

        const filter = (new FilterBuilder());

        filter.setQuery('ownerOrgKey', action.orgKey);

        return this._protocol_preferencesService.query(filter.get())
            .pipe(
                tap(
                    result => {
                        ctx.patchState({
                            preferences: result.data,
                        })

                        this.loadProtocolOptions(ctx, state.orgKey, result.data);
                    },
                    error => {
                        ctx.patchState({
                            preferences: state.preferences ? state.preferences : [],
                            status: translateCommonErrorStatus(error)
                        });
                    }
                )
            )
    }

        private loadProtocolOptions(ctx: StateContext<EvaluationScheduleFormStateModel>, orgKey: string, preferences: OrganizationProtocolPreference[]) {
            const state = ctx.getState();

            const filterCrops = state.cropIds && state.cropIds.length > 0
                ? state.cropIds
                : (state.cropId ? [state.cropId] : []);

            const filter: Partial<ProtocolFilter> = {
                showFavourites: true,
                showHidden: false,
                crops: filterCrops,
                sampleTypes: state.sampleType ? [state.sampleType] : [],
            }

            this._customProtocolService.allProtocolsObserved(orgKey, preferences, filter).subscribe(all => {

                // allProtocolsObserved()'s crop filter is a union across crops — a
                // multi-crop bulk selection needs the true intersection instead, so narrow
                // the result down to protocols common to every selected crop.
                if (state.cropIds && state.cropIds.length > 1) {
                    const { commonProtocolIds } = this._library.isSelectionCompatible(state.cropIds);
                    const commonProtocolIdSet = new Set(commonProtocolIds);
                    all = all.filter(protocol => commonProtocolIdSet.has(protocol.id));
                }

                if (!all || all.length == 0) this._snackbar.error("No protocols found, contact your Organisation's Administrators to update your protocol preferences.")

                ctx.patchState({
                    status: Status.OK,
                    protocolOptions: all,
                })
            });
        }

}